// src/utils/sharePointTree.ts
import axios from "axios";
import type { AxiosResponse } from "axios";
import { getToken } from "./tokenHelper";
import type { VDRProviderId } from "../vdr/providers";

// ---------- Types ----------

export type SPFile = { id: string; name: string; webUrl?: string };

export type SPTreeNode = {
  id: string;        // SharePoint itemId
  name: string;      // folder display name
  children: SPTreeNode[];
  files?: SPFile[];  // Only populated when fetched with includeFiles=true
};

type GraphChild = { id: string; name: string; folder?: any; file?: any; webUrl?: string };
type GraphChildrenResponse = { value?: GraphChild[]; "@odata.nextLink"?: string };

// ---------- Concurrency & rate limiting ----------

const FETCH_CONCURRENCY = 10;

// Shared rate limiter — exported so folderSetup.ts and any other Graph callers
// use the same gate. Caps ALL Graph API calls across the app to 8/sec.
// At 8/sec a 3000-folder tree completes in ~6 minutes with no throttling.
class RateLimiter {
  private tokens: number;
  private lastRefill: number;
  private readonly ratePerMs: number;
  private readonly maxTokens: number;

  constructor(requestsPerSecond: number) {
    this.ratePerMs = requestsPerSecond / 1000;
    this.maxTokens = requestsPerSecond;
    this.tokens = requestsPerSecond;
    this.lastRefill = Date.now();
  }

  async acquire(): Promise<void> {
    while (true) {
      const now = Date.now();
      this.tokens = Math.min(
        this.maxTokens,
        this.tokens + (now - this.lastRefill) * this.ratePerMs
      );
      this.lastRefill = now;
      if (this.tokens >= 1) {
        this.tokens -= 1;
        return;
      }
      await new Promise((r) => setTimeout(r, Math.ceil((1 - this.tokens) / this.ratePerMs)));
    }
  }
}

export const graphRateLimiter = new RateLimiter(8);

// ---------- Recursive SharePoint fetch ----------

async function listFolderChildren(
  driveId: string,
  parentItemId: string,
  token: string
): Promise<GraphChild[]> {
  const result: GraphChild[] = [];
  let url: string | null =
    `https://graph.microsoft.com/v1.0/drives/${driveId}/items/${parentItemId}/children?` +
    `$select=id,name,folder,file,webUrl&$top=999`;

  while (url) {
    await graphRateLimiter.acquire();

    const res: AxiosResponse<GraphChildrenResponse> = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
      validateStatus: () => true,
    });

    if (res.status === 429) {
      const retryAfter = parseInt(res.headers["retry-after"] || "5", 10);
      console.warn(`   ⏳ Throttled — retrying in ${retryAfter}s`);
      await new Promise((r) => setTimeout(r, retryAfter * 1000));
      continue;
    }

    if (res.status !== 200) {
      console.error(`   ❌ listFolderChildren failed`, res.status, res.data);
      throw new Error(`Failed to list children (${res.status})`);
    }

    const chunk = res.data.value || [];
    chunk.forEach((c) => result.push(c));
    url = res.data["@odata.nextLink"] || null;
  }

  return result;
}

/**
 * Recursively fetch the entire folder tree under a SharePoint location.
 * Returns a tree of SPTreeNode with only folders (files are ignored).
 *
 * @param onProgress  Optional callback with running folder count for UI updates.
 */
export async function fetchSharePointTree(
  driveId: string,
  rootItemId: string,
  onProgress?: (folderCount: number) => void,
  includeFiles: boolean = false
): Promise<SPTreeNode> {
  const token = await getToken();
  let totalFolders = 0;
  let totalFiles = 0;

  async function buildNode(
    itemId: string,
    name: string
  ): Promise<SPTreeNode> {
    const allChildren = await listFolderChildren(driveId, itemId, token);
    const folderChildren = allChildren.filter((c) => c.folder);
    const fileChildren = includeFiles ? allChildren.filter((c) => c.file) : [];

    totalFolders += folderChildren.length;
    totalFiles += fileChildren.length;
    onProgress?.(totalFolders);

    // Process children with concurrency cap
    const children: SPTreeNode[] = [];
    const queue = [...folderChildren];

    const worker = async () => {
      while (true) {
        const child = queue.shift();
        if (!child) break;
        children.push(await buildNode(child.id, child.name));
      }
    };

    const workerCount = Math.min(FETCH_CONCURRENCY, queue.length || 1);
    await Promise.all(Array.from({ length: workerCount }, () => worker()));

    const node: SPTreeNode = { id: itemId, name, children };
    if (includeFiles) {
      node.files = fileChildren.map((f) => ({ id: f.id, name: f.name, webUrl: f.webUrl }));
    }
    return node;
  }

  console.log(`🔄 Fetching SharePoint tree from root=${rootItemId} (includeFiles=${includeFiles})`);
  const tree = await buildNode(rootItemId, "(root)");
  console.log(`✅ SharePoint tree fetched: ${totalFolders} folders${includeFiles ? `, ${totalFiles} files` : ""}`);
  return tree;
}

// ---------- Code extraction ----------

/** Extract leading number from a folder name: "1.2.3 Contracts" → "1.2.3" */
const CODE_RE = /^\s*(\d+(?:\.\d+)*)/;

function extractLeadingCode(name: string): string | null {
  const m = name.match(CODE_RE);
  return m ? m[1] : null;
}

/**
 * Walk a SharePoint tree and extract an indexMap (code → itemId).
 * Non-numbered folders are skipped entirely (not recursed into).
 *
 * For standard providers: code is extracted directly from folder name.
 * For Firmex: absolute code is reconstructed from relative numbering.
 *
 * @param indexRows  Optional Firmex index rows used for title-based matching when SP
 *                   folders were created with original titles (no number prefix).
 */
export function extractCodesFromTree(
  tree: SPTreeNode,
  providerId: VDRProviderId,
  useFilerooms: boolean = false,
  indexRows?: { code: string; title: string; meta?: Record<string, unknown> }[]
): Record<string, string> {
  const map: Record<string, string> = {};

  if (providerId === "firmex") {
    extractFirmexCodes(tree.children, map, null, indexRows);
  } else if (providerId === "requestlist") {
    extractRequestListCodes(tree.children, map, indexRows ?? []);
  } else if (providerId === "dataroom" && useFilerooms) {
    extractDataroomCodes(tree.children, map);
  } else {
    extractStandardCodes(tree.children, map);
  }

  console.log(`📋 Extracted ${Object.keys(map).length} folder codes from SharePoint tree`);
  return map;
}

/** Standard providers: code comes directly from folder name */
function extractStandardCodes(
  nodes: SPTreeNode[],
  map: Record<string, string>
): void {
  for (const node of nodes) {
    const code = extractLeadingCode(node.name);
    if (!code) continue; // Non-numbered folder — skip entirely

    map[code] = node.id;
    extractStandardCodes(node.children, map);
  }
}

/**
 * Request-list VDRs: folder names carry no usable codes ("1. Financial Information"
 * and "1 - Policy…" would both read as "1"), so match category and request folders
 * to index rows by their exact VDR folder name. Without index rows nothing maps.
 */
function extractRequestListCodes(
  nodes: SPTreeNode[],
  map: Record<string, string>,
  indexRows: { code: string; meta?: Record<string, unknown> }[]
): void {
  const named = (r: { meta?: Record<string, unknown> }, node: SPTreeNode) =>
    String(r.meta?.folderName ?? "").toLowerCase() === node.name.toLowerCase();

  for (const cat of nodes) {
    const catRow = indexRows.find((r) => !r.code.includes(".") && named(r, cat));
    if (!catRow) continue;
    map[catRow.code] = cat.id;
    for (const item of cat.children) {
      const itemRow = indexRows.find((r) => r.code.startsWith(`${catRow.code}.`) && named(r, item));
      if (itemRow) map[itemRow.code] = item.id;
    }
  }
}

/**
 * Convert a fetched SP tree into a childrenCache map compatible with
 * createFoldersFromIndexById and createFirmexFoldersById.
 * Pre-populating the cache means folder setup never needs to re-list
 * children that were already fetched during the sync tree walk.
 */
export function buildChildrenCacheFromTree(
  tree: SPTreeNode
): Map<string, { id: string; name: string; folder?: any }[]> {
  const cache = new Map<string, { id: string; name: string; folder?: any }[]>();

  function walk(node: SPTreeNode) {
    if (node.children.length > 0) {
      cache.set(node.id, node.children.map(c => ({ id: c.id, name: c.name, folder: true })));
    }
    for (const child of node.children) {
      walk(child);
    }
  }

  walk(tree);
  return cache;
}

/**
 * Flatten a SharePoint tree into a map of folder paths to item IDs.
 * Path format: "Clients/ABC Corp/Financials" (no leading slash, no root name).
 *
 * Used by Folder Mirroring to match local folder paths to SharePoint folders.
 */
export function flattenTreeByPath(tree: SPTreeNode): Map<string, string> {
  const map = new Map<string, string>();

  function walk(nodes: SPTreeNode[], prefix: string) {
    for (const node of nodes) {
      const path = prefix ? `${prefix}/${node.name}` : node.name;
      map.set(path, node.id);
      map.set(path.toLowerCase(), node.id); // case-insensitive lookup
      walk(node.children, path);
    }
  }

  walk(tree.children, "");
  return map;
}

/**
 * Flatten a SharePoint tree into a set of file paths (for dup-check).
 * Requires the tree to be fetched with includeFiles=true.
 * Returns a Set of lowercase paths like "abc corp/financials/q4.pdf".
 */
export function flattenTreeFilePaths(tree: SPTreeNode): Set<string> {
  const set = new Set<string>();

  // Include files directly at the root of the picked folder
  if (tree.files) {
    for (const f of tree.files) {
      set.add(f.name.toLowerCase());
    }
  }

  function walk(nodes: SPTreeNode[], prefix: string) {
    for (const node of nodes) {
      const folderPath = prefix ? `${prefix}/${node.name}` : node.name;
      if (node.files) {
        for (const f of node.files) {
          set.add(`${folderPath}/${f.name}`.toLowerCase());
        }
      }
      walk(node.children, folderPath);
    }
  }

  walk(tree.children, "");
  return set;
}

/**
 * Dataroom: root folders may be unnumbered FILEROOMs (e.g. "ABC Corp").
 * - Numbered root folders → standard code (e.g. "1" → map["1"])
 * - Unnumbered root folders → FILEROOM; store as map["__fr:Name"] and
 *   recurse into children with scoped keys map["__fr:Name::1.2.3"]
 *
 * This mirrors the scoped-key format used by folderSetup.ts and uploadRouter.ts.
 */
function extractDataroomCodes(nodes: SPTreeNode[], map: Record<string, string>): void {
  for (const node of nodes) {
    const code = extractLeadingCode(node.name);
    if (!code) {
      // Unnumbered root folder = FILEROOM (e.g. "Phase I")
      map[`__fr:${node.name}`] = node.id;
      extractDataroomScopedCodes(node.children, map, node.name);
    } else {
      // Check for Datasite bulk-download format: "2_Phase I" (number + underscore prefix).
      // Strip the prefix to recover the FILEROOM name and treat as a FILEROOM.
      const underscoreStripped = node.name.replace(/^\d+(?:\.\d+)*_/, "").trim();
      if (underscoreStripped && underscoreStripped !== node.name) {
        map[`__fr:${underscoreStripped}`] = node.id;
        extractDataroomScopedCodes(node.children, map, underscoreStripped);
      } else {
        // Regular numbered folder with space/dot separator (e.g. "1 Financial Statements")
        map[code] = node.id;
        extractStandardCodes(node.children, map);
      }
    }
  }
}

function extractDataroomScopedCodes(
  nodes: SPTreeNode[],
  map: Record<string, string>,
  fileroomName: string
): void {
  for (const node of nodes) {
    const code = extractLeadingCode(node.name);
    if (!code) continue;
    map[`__fr:${fileroomName}::${code}`] = node.id;
    extractDataroomScopedCodes(node.children, map, fileroomName);
  }
}

/**
 * Firmex: reconstruct absolute codes from relative numbering.
 *
 * Root children: "6. Taxes" → absolute code "6"
 * Under root 6: "12. Payroll Returns" → absolute code "6.12"
 * Under 6.12: "12.1. Monthly IS" → absolute code "6.12.1"
 *
 * Unnumbered root folders (e.g. "R2 VDR") are treated as named scopes:
 * their children get keys like "__root:R2 VDR::14", "__root:R2 VDR::14.32".
 *
 * When indexRows is provided, SP folders with no number prefix are matched
 * by title so that original-Firmex-named SP folders are still found.
 */
function extractFirmexCodes(
  nodes: SPTreeNode[],
  map: Record<string, string>,
  rootNum: string | null,
  indexRows?: { code: string; title: string }[]
): void {
  if (rootNum === null) {
    console.log(`[firmex-codes] root-level SP folders: ${nodes.map(n => `"${n.name}"(${n.children.length} children)`).join(", ")}`);
  }
  for (const node of nodes) {
    const displayNum = extractLeadingCode(node.name);
    if (!displayNum) {
      // Unnumbered folder — only handle at root level as a named scope
      if (rootNum === null) {
        map[`__root:${node.name}`] = node.id;
        // Find this root's absolute code from indexRows for title-based child matching
        const rootRow = indexRows?.find(
          r => !r.code.includes(".") && r.title.toLowerCase().trim() === node.name.toLowerCase().trim()
        );
        console.log(`[firmex-codes] unnumbered root "${node.name}": ${node.children.length} SP children, rootRow=${JSON.stringify(rootRow)}`);
        extractFirmexScopedCodes(node.children, map, node.name, indexRows, rootRow?.code, "");
      }
      continue;
    }

    let absoluteCode: string;
    if (rootNum === null) {
      absoluteCode = displayNum;
      map[absoluteCode] = node.id;
      // Also index this root's children under a scoped key using the root's title
      // (e.g. "2. R2 VDR" → rootTitle "R2 VDR" → children get "__root:R2 VDR::1" etc.)
      const rootTitle = node.name.replace(/^\s*\d+(?:\.\d+)*\.?\s+/, "").trim();
      if (rootTitle) {
        map[`__root:${rootTitle}`] = node.id;
        extractFirmexScopedCodes(node.children, map, rootTitle, indexRows, absoluteCode, "");
      }
      extractFirmexCodes(node.children, map, displayNum, indexRows);
    } else {
      absoluteCode = `${rootNum}.${displayNum}`;
      map[absoluteCode] = node.id;
      extractFirmexCodes(node.children, map, rootNum, indexRows);
    }
  }
}

/**
 * Write a scoped key → folder ID into the map, warning if two DIFFERENT SP
 * folders try to claim the same key. That collision is the tell-tale sign of a
 * mislabeled folder in SharePoint (e.g. a deep folder a human named just "1"),
 * which is exactly what silently misroutes creation. Logging it makes it visible.
 */
function assignScopedKey(
  map: Record<string, string>,
  rootName: string,
  relCode: string,
  node: SPTreeNode
): void {
  const key = `__root:${rootName}::${relCode}`;
  const existing = map[key];
  if (existing && existing !== node.id) {
    console.warn(
      `[firmex-tree] ⚠️ COLLISION on "${key}": already mapped → ${existing}, ` +
      `now folder "${node.name}" → ${node.id}. Likely a mislabeled folder in SharePoint. Keeping the later one.`
    );
  }
  map[key] = node.id;
}

/**
 * Index children of a Firmex root folder using scoped keys, e.g.
 *   "1. Corporate Documents"  → "__root:R2 VDR::1"
 *   "1.6. Tax" under it        → "__root:R2 VDR::1.6"
 *
 * A folder's code is derived from its POSITION in the tree — the parent's
 * relative code plus THIS folder's own local number (last segment of its label)
 * — NOT from the folder's full label. Position-based keys mean a folder a human
 * mislabeled "1" deep in the tree becomes e.g. "3.4.5.1" (its real spot) instead
 * of colliding with the real top-level "1" and clobbering it. For correctly
 * labeled folders this yields the identical key it always did.
 *
 * When indexRows + rootAbsCode are provided, SP folders with no number prefix
 * are matched by title against indexRows so that SP folders created with original
 * Firmex titles (e.g. "Corporate Documents" instead of "1. Corporate Documents")
 * are still correctly mapped.
 *
 * @param rootAbsCode   Absolute code of the VDR root in the Firmex index (e.g. "2")
 * @param parentRelCode Code of the immediate parent RELATIVE to the root
 *                      ("" for the root's direct children, "1.6" deeper in)
 */
function extractFirmexScopedCodes(
  nodes: SPTreeNode[],
  map: Record<string, string>,
  rootName: string,
  indexRows?: { code: string; title: string }[],
  rootAbsCode?: string,
  parentRelCode?: string
): void {
  for (const node of nodes) {
    const leadingCode = extractLeadingCode(node.name);

    if (!leadingCode) {
      // No number prefix — title-match against index rows to find this folder's code.
      if (indexRows && rootAbsCode !== undefined) {
        // Reconstruct the parent's absolute code (root counter + relative) so we
        // can search the index rows for this folder's direct children.
        const parentAbsCode = parentRelCode ? `${rootAbsCode}.${parentRelCode}` : rootAbsCode;
        const prefix = parentAbsCode + ".";
        const matchRow = indexRows.find(r => {
          if (!r.code.startsWith(prefix)) return false;
          const after = r.code.slice(prefix.length);
          if (after.includes(".")) return false; // only direct children
          return r.title.toLowerCase().trim() === node.name.toLowerCase().trim();
        });
        console.log(`[firmex-tree] unnumbered SP folder "${node.name}" under ${parentAbsCode}: ${matchRow ? `matched → ${matchRow.code}` : "NO MATCH"}`);
        if (matchRow) {
          const relCode = matchRow.code.slice(rootAbsCode.length + 1);
          assignScopedKey(map, rootName, relCode, node);
          extractFirmexScopedCodes(node.children, map, rootName, indexRows, rootAbsCode, relCode);
        }
      } else {
        console.log(`[firmex-tree] unnumbered SP folder "${node.name}" — skipped (indexRows=${!!indexRows} parentRelCode=${parentRelCode} rootAbsCode=${rootAbsCode})`);
      }
      continue;
    }

    // Position-based code: parent's relative code + THIS folder's own local number.
    const localNum = leadingCode.split(".").pop()!;
    const relCode = parentRelCode ? `${parentRelCode}.${localNum}` : localNum;
    assignScopedKey(map, rootName, relCode, node);
    extractFirmexScopedCodes(node.children, map, rootName, indexRows, rootAbsCode, relCode);
  }
}
